import React, { PropsWithChildren } from "react";

import { useLocation } from "react-router-dom";

import { StateApiErrorType } from "commons/hooks/useNavigateToApiErrorPage";

type ApiErrorContextType = {
    withError: boolean;
    state: StateApiErrorType | undefined;
};

type ApiErrorActionsContextType = {
    showError: (state: StateApiErrorType) => void;
    hideError: () => void;
};

function getDefaultApiError(): ApiErrorContextType {
    return {
        withError: false,
        state: undefined,
    };
}

export const ApiErrorContext = React.createContext<ApiErrorContextType>(getDefaultApiError());

export const ApiErrorActionsContext = React.createContext<ApiErrorActionsContextType | undefined>(
    undefined
);

export const useApiErrorContext = () => React.useContext(ApiErrorContext);

export const useApiErrorActionsContext = () => React.useContext(ApiErrorActionsContext);

export const ApiErrorContextProvider = (props: PropsWithChildren<{}>) => {
    const location = useLocation();

    const [apiError, setApiError] = React.useState<ApiErrorContextType>(getDefaultApiError());

    const showError = React.useCallback((state: StateApiErrorType) => {
        setApiError({
            withError: true,
            state,
        });
    }, []);

    const hideError = React.useCallback(() => {
        setApiError(getDefaultApiError());
    }, []);

    React.useEffect(() => {
        hideError();
    }, [hideError, location.pathname]);

    const apiErrorActionsValue = React.useMemo(() => {
        return { showError, hideError };
    }, [showError, hideError]);

    return (
        <ApiErrorContext.Provider value={apiError}>
            <ApiErrorActionsContext.Provider value={apiErrorActionsValue}>
                {props.children}
            </ApiErrorActionsContext.Provider>
        </ApiErrorContext.Provider>
    );
};
